import { useCallback, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import { FinalidadeEstado, type EstadoConfigDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { useConfirmar } from '../design-system/Confirmacao';
import { CampoConfig, Grupo, useCarga, useGravar } from './campos';

type Linha = { nome: string; cor: string; exigeMotivo: boolean; motivos: string };
const FIXOS: Record<number, string> = {
  [FinalidadeEstado.Novo]: 'Entrada',
  [FinalidadeEstado.SemEstado]: 'Bandeja',
  [FinalidadeEstado.Arquivo]: 'Arquivo',
  [FinalidadeEstado.Lixeira]: 'Lixeira',
};

type PropsEstado = { e: EstadoConfigDto; podeSubir: boolean; podeDescer: boolean; mover: (passo: -1 | 1) => void; aoMudar: () => void };

function Estado({ e, podeSubir, podeDescer, mover, aoMudar }: PropsEstado) {
  const { configuracoes } = useDependencias();
  const confirmar = useConfirmar();
  const { gravar, gravando } = useGravar();
  const [l, definir] = useState<Linha>({ nome: e.nome, cor: e.cor, exigeMotivo: e.exigeMotivo, motivos: e.motivos.join('\n') });
  const comMotivos = e.finalidade === FinalidadeEstado.Arquivo || e.motivos.length > 0;
  const salvar = async () => {
    const motivos = comMotivos ? l.motivos.split('\n').map((m) => m.trim()).filter(Boolean) : undefined;
    if (await gravar(() => configuracoes.alterarEstado(e.uuid, { nome: l.nome.trim(), cor: l.cor, exigeMotivo: l.exigeMotivo, motivos }), `${l.nome.trim()} salvo.`)) aoMudar();
  };
  const excluir = async () => {
    const sim = await confirmar({ titulo: `Excluir o estado ${e.nome}?`, mensagem: 'O estado e o grupo dele saem do quadro.', rotulo: 'Excluir' });
    if (sim && (await gravar(() => configuracoes.excluirEstado(e.uuid), `${e.nome} excluído.`))) aoMudar();
  };
  const fluxo = e.finalidade === FinalidadeEstado.Fluxo;
  return (
    <li className="config__estado">
      <input type="color" className="config__cor" value={l.cor} aria-label={`Cor de ${e.nome}`} onChange={(ev) => definir({ ...l, cor: ev.target.value.toUpperCase() })} />
      <div className="config__estado-dados">
        <CampoConfig rotulo={e.fixo ? `Nome (${FIXOS[e.finalidade]})` : 'Nome'} valor={l.nome} definir={(nome) => definir({ ...l, nome })} />
        <label className="config__marcar">
          <input type="checkbox" checked={l.exigeMotivo} onChange={(ev) => definir({ ...l, exigeMotivo: ev.target.checked })} /> Exige motivo ao entrar
        </label>
        <small>{e.processos} processo(s)</small>
        {comMotivos && (
          <label className="campo config__campo config__campo--longa">
            <span>Motivos (um por linha)</span>
            <textarea rows={4} value={l.motivos} onChange={(ev) => definir({ ...l, motivos: ev.target.value })} />
          </label>
        )}
      </div>
      <div className="config__acoes-linha">
        {fluxo && (
          <>
            <button type="button" className="botao-icone" aria-label={`Subir ${e.nome}`} disabled={!podeSubir} onClick={() => mover(-1)}>
              <ArrowUp size={16} />
            </button>
            <button type="button" className="botao-icone" aria-label={`Descer ${e.nome}`} disabled={!podeDescer} onClick={() => mover(1)}>
              <ArrowDown size={16} />
            </button>
          </>
        )}
        <button type="button" className="botao-icone" aria-label={`Salvar ${e.nome}`} disabled={gravando || !l.nome.trim()} onClick={() => void salvar()}>
          <Save size={16} />
        </button>
        {fluxo && e.processos === 0 && (
          <button type="button" className="botao-icone" aria-label={`Excluir ${e.nome}`} onClick={() => void excluir()}>
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

function Estados({ quadroUuid }: { quadroUuid: string }) {
  const { configuracoes } = useDependencias();
  const carregar = useCallback(() => configuracoes.estados(quadroUuid), [configuracoes, quadroUuid]);
  const { dados, erro, recarregar } = useCarga(carregar);
  const { gravar } = useGravar();
  const [novo, definirNovo] = useState({ nome: '', cor: '#5B4CF0' });
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  const fluxo = dados.filter((e) => e.finalidade === FinalidadeEstado.Fluxo);
  const mover = async (uuid: string, passo: -1 | 1) => {
    const ordem = fluxo.map((e) => e.uuid);
    const i = ordem.indexOf(uuid);
    [ordem[i], ordem[i + passo]] = [ordem[i + passo]!, ordem[i]!];
    if (await gravar(() => configuracoes.ordenarEstados(quadroUuid, ordem), 'Ordem salva.')) await recarregar();
  };
  const criar = async () => {
    if (await gravar(() => configuracoes.criarEstado(quadroUuid, { nome: novo.nome.trim(), cor: novo.cor.toUpperCase() }), `Estado ${novo.nome.trim()} criado, com o grupo no quadro.`)) {
      definirNovo({ nome: '', cor: '#5B4CF0' });
      await recarregar();
    }
  };
  return (
    <>
      <ul className="config__usuarios" key={dados.map((e) => `${e.uuid}${e.ordem}${e.nome}`).join()}>
        {dados.map((e) => {
          const i = fluxo.indexOf(e);
          return <Estado key={e.uuid} e={e} podeSubir={i > 0} podeDescer={i >= 0 && i < fluxo.length - 1} mover={(p) => void mover(e.uuid, p)} aoMudar={() => void recarregar()} />;
        })}
      </ul>
      <Grupo
        titulo="Novo estado do fluxo"
        acoes={
          <button type="button" className="botao botao--primario" disabled={!novo.nome.trim()} onClick={() => void criar()}>
            <Plus size={16} aria-hidden /> Criar estado
          </button>
        }
      >
        <CampoConfig rotulo="Nome" valor={novo.nome} definir={(nome) => definirNovo({ ...novo, nome })} />
        <label className="campo config__campo config__campo--curta">
          <span>Cor</span>
          <input type="color" className="config__cor" value={novo.cor} onChange={(e) => definirNovo({ ...novo, cor: e.target.value })} />
        </label>
      </Grupo>
    </>
  );
}

/** Estados de cada quadro (ADR-0022): administrador em qualquer quadro; gestor, nos quadros que gere. */
export function SecaoEstados() {
  const { espacos } = useDependencias();
  const quadros = espacos.empresas.find((e) => e.uuid === espacos.atual.empresaUuid)?.quadros ?? [];
  const [quadro, definirQuadro] = useState(espacos.atual.quadroUuid);
  return (
    <div className="config__formulario">
      <label className="campo config__campo config__campo--media">
        <span>Quadro</span>
        <select value={quadro} onChange={(e) => definirQuadro(e.target.value)}>
          {quadros.map((q) => (
            <option key={q.uuid} value={q.uuid}>
              {q.nome}
            </option>
          ))}
        </select>
      </label>
      <p className="config__nota">Novo, Aguardando análise, Arquivo e Lixeira podem ser renomeados, mas não excluídos. Estado com processos só sai depois de mover os processos.</p>
      <Estados key={quadro} quadroUuid={quadro} />
    </div>
  );
}
